import { isArray } from 'lodash';

import { ApolloError } from '@apollo/client';
import { DocumentNode } from 'graphql';

import { getErrorType, getErrorMessage } from '~/util/errorHandling';
import { getOperationNameFromDocument } from './documentParsers';
import { reporter } from '~/hooks/useErrorReporter';

type Options = {
  name: string;
  doc: DocumentNode;
  doNotReportErrorTypes?: Array<string>;
  doNotReportErrorMessages?: Array<string>;
  ignoreAutomaticErrorReporting?: boolean;
  onError?: (error: ApolloError) => void;
  variables?: $Object;
};
const errorReporter = (error: ApolloError, options: Options) => {
  const {
    doNotReportErrorTypes = [],
    doNotReportErrorMessages = [],
    ignoreAutomaticErrorReporting = false,
    onError,
    variables,
    doc,
  } = options;

  if (ignoreAutomaticErrorReporting) {
    if (onError !== undefined) onError(error);

    return null;
  }

  const operationName = getOperationNameFromDocument(doc);

  // --- Define the error reporter options object ---
  const errorOptions = {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    errorInfo: {
      ...error.extraInfo,
      operationName,
      documentVariables: variables,
    },
  };

  if (error.networkError)
    reporter.captureException(error.networkError, 'fatal', errorOptions);

  if (isArray(error.graphQLErrors)) {
    error.graphQLErrors.forEach(graphqlError => {
      let shouldIgnore = false;

      const errorType = getErrorType(graphqlError);

      if (errorType != null && doNotReportErrorTypes.includes(errorType)) {
        shouldIgnore = true;
      }

      if (
        doNotReportErrorMessages.some(message => {
          const errorMessage = getErrorMessage(graphqlError);
          return errorMessage != null && errorMessage.includes(message);
        })
      ) {
        shouldIgnore = true;
      }

      if (!shouldIgnore) {
        reporter.captureException(
          new Error(`[GraphQL] ${graphqlError.message}`),
          'error',
          errorOptions,
        );
      }
    });
  }

  if (typeof onError === 'function') onError(error);

  return;
};

export default errorReporter;
